// TelcoTrack — potvrzení převodu mezi osobními sklady (přijetí / výdej)
//
// Osobní ↔ osobní: zásoba se nepřesune, dokud protistrana nepotvrdí.
// Pull (iniciátor = Kam): zdroj potvrzuje výdej.
// Push / admin (iniciátor ≠ Kam): cíl potvrzuje přijetí.
// Převod s neosobním skladem zůstává okamžitý.

import { hasWarehouseEditRole } from '../constants/roles.constants';
import { isPersonalWarehouse, type WarehouseUiGroupInput } from './warehouse-ui-group.util';
import type { WarehouseIssueActor } from './warehouse-issue-acl.util';

export const WAREHOUSE_TRANSFER_CONFIRMATION_PRIJEM = 'prijem' as const;
export const WAREHOUSE_TRANSFER_CONFIRMATION_VYDEJ = 'vydej' as const;

export type WarehouseTransferConfirmationKind =
  | typeof WAREHOUSE_TRANSFER_CONFIRMATION_PRIJEM
  | typeof WAREHOUSE_TRANSFER_CONFIRMATION_VYDEJ;

export const WAREHOUSE_TRANSFER_CONFIRMATION_ACTION_CS: Record<
  WarehouseTransferConfirmationKind,
  string
> = {
  prijem: 'Potvrdit přijetí',
  vydej: 'Potvrdit výdej',
};

export const WAREHOUSE_TRANSFER_CONFIRMATION_PENDING_CS: Record<
  WarehouseTransferConfirmationKind,
  string
> = {
  prijem: 'Čeká na potvrzení přijetí',
  vydej: 'Čeká na potvrzení výdeje',
};

export const WAREHOUSE_TRANSFER_REJECT_ACTION_CS = 'Odmítnout';

export const WAREHOUSE_TRANSFER_NOTIFICATION_OBJECT_TYPE = 'WAREHOUSE';
export const WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_REQUEST = 'TRANSFER_CONFIRM';
export const WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_REQUEST_PRIJEM = 'TRANSFER_CONFIRM_PRIJEM';
export const WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_REQUEST_VYDEJ = 'TRANSFER_CONFIRM_VYDEJ';
export const WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_DONE = 'TRANSFER_CONFIRMED';
export const WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_REJECTED = 'TRANSFER_REJECTED';
/** Okamžitý převod (bez potvrzení) — materiál dorazil na osobní sklad. */
export const WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_RECEIVED = 'TRANSFER_RECEIVED';
/** Okamžitý převod (bez potvrzení) — materiál odebrán z osobního skladu. */
export const WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_TAKEN = 'TRANSFER_TAKEN';

export type WarehouseTransferNotificationOutcome =
  | 'request'
  | 'confirmed'
  | 'rejected'
  | 'received'
  | 'taken';

export interface WarehouseTransferNotificationItemInput {
  productName?: string | null;
  sapCode?: string | null;
  quantity?: number | null;
  unit?: string | null;
}

export const WAREHOUSE_TRANSFER_PENDING_STATUSES = ['Draft', 'Reserving', 'Reserved'] as const;

export interface WarehouseTransferConfirmationDecision {
  required: boolean;
  kind: WarehouseTransferConfirmationKind | null;
  confirmerWarehouseId: string | null;
}

export function isPendingWarehouseTransferStatus(status: string | null | undefined): boolean {
  const raw = String(status ?? '').trim();
  if (!raw) return false;
  return (WAREHOUSE_TRANSFER_PENDING_STATUSES as readonly string[]).some(
    (s) => s.toLowerCase() === raw.toLowerCase(),
  );
}

export function resolveWarehouseTransferConfirmation(params: {
  fromWarehouse: (WarehouseUiGroupInput & { id: string }) | null | undefined;
  toWarehouse: (WarehouseUiGroupInput & { id: string }) | null | undefined;
  initiatorWarehouseId?: string | null;
}): WarehouseTransferConfirmationDecision {
  const fromWh = params.fromWarehouse;
  const toWh = params.toWarehouse;
  if (!fromWh?.id || !toWh?.id) {
    return { required: false, kind: null, confirmerWarehouseId: null };
  }
  if (!isPersonalWarehouse(fromWh) || !isPersonalWarehouse(toWh)) {
    return { required: false, kind: null, confirmerWarehouseId: null };
  }

  const initiatorId = String(params.initiatorWarehouseId ?? '').trim();
  const fromId = fromWh.id.trim();
  const toId = toWh.id.trim();

  // Pull: beru z kolegova skladu na svůj → kolega potvrzuje výdej.
  if (initiatorId && initiatorId === toId && initiatorId !== fromId) {
    return {
      required: true,
      kind: WAREHOUSE_TRANSFER_CONFIRMATION_VYDEJ,
      confirmerWarehouseId: fromId,
    };
  }

  // Push / admin: posílám na cizí osobní sklad → příjemce potvrzuje přijetí.
  return {
    required: true,
    kind: WAREHOUSE_TRANSFER_CONFIRMATION_PRIJEM,
    confirmerWarehouseId: toId,
  };
}

export function warehouseTransferConfirmationActionLabel(
  kind: WarehouseTransferConfirmationKind | null | undefined,
): string {
  if (kind === WAREHOUSE_TRANSFER_CONFIRMATION_VYDEJ) {
    return WAREHOUSE_TRANSFER_CONFIRMATION_ACTION_CS.vydej;
  }
  if (kind === WAREHOUSE_TRANSFER_CONFIRMATION_PRIJEM) {
    return WAREHOUSE_TRANSFER_CONFIRMATION_ACTION_CS.prijem;
  }
  return 'Potvrdit převod';
}

export function canActorConfirmWarehouseTransfer(params: {
  actor: WarehouseIssueActor | null | undefined;
  actorWarehouseId?: string | null;
  confirmerWarehouseId?: string | null;
  pending: boolean;
}): boolean {
  if (!params.pending) return false;
  const confirmerId = String(params.confirmerWarehouseId ?? '').trim();
  if (!confirmerId) return false;
  const roles = params.actor?.roles ? [...params.actor.roles] : [];
  if (hasWarehouseEditRole(roles)) return true;
  const actorWh = String(params.actorWarehouseId ?? params.actor?.warehouseId ?? '').trim();
  return !!actorWh && actorWh === confirmerId;
}

export function canActorRejectWarehouseTransfer(params: {
  actor: WarehouseIssueActor | null | undefined;
  actorWarehouseId?: string | null;
  actorEmail?: string | null;
  confirmerWarehouseId?: string | null;
  performedBy?: string | null;
  pending: boolean;
}): boolean {
  if (!params.pending) return false;
  if (
    canActorConfirmWarehouseTransfer({
      actor: params.actor,
      actorWarehouseId: params.actorWarehouseId,
      confirmerWarehouseId: params.confirmerWarehouseId,
      pending: params.pending,
    })
  ) {
    return true;
  }
  const actorEmail = String(params.actorEmail ?? '').trim().toLowerCase();
  const initiatedBy = String(params.performedBy ?? '').trim().toLowerCase();
  return !!actorEmail && !!initiatedBy && actorEmail === initiatedBy;
}

export function warehouseTransferConfirmNotificationPhase(
  kind: WarehouseTransferConfirmationKind | null | undefined,
): string {
  if (kind === WAREHOUSE_TRANSFER_CONFIRMATION_VYDEJ) {
    return WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_REQUEST_VYDEJ;
  }
  if (kind === WAREHOUSE_TRANSFER_CONFIRMATION_PRIJEM) {
    return WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_REQUEST_PRIJEM;
  }
  return WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_REQUEST;
}

export function isWarehouseTransferConfirmNotification(
  phaseCode?: string | null,
): boolean {
  const code = String(phaseCode ?? '').trim();
  return (
    code === WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_REQUEST ||
    code === WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_REQUEST_PRIJEM ||
    code === WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_REQUEST_VYDEJ
  );
}

export function warehouseTransferConfirmKindFromPhase(
  phaseCode?: string | null,
): WarehouseTransferConfirmationKind | null {
  const code = String(phaseCode ?? '').trim();
  if (code === WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_REQUEST_VYDEJ) {
    return WAREHOUSE_TRANSFER_CONFIRMATION_VYDEJ;
  }
  if (code === WAREHOUSE_TRANSFER_NOTIFICATION_PHASE_REQUEST_PRIJEM) {
    return WAREHOUSE_TRANSFER_CONFIRMATION_PRIJEM;
  }
  return null;
}

export function formatWarehouseTransferItemSummary(
  items?: ReadonlyArray<WarehouseTransferNotificationItemInput> | null,
): string {
  const parts = (items ?? [])
    .map((item) => {
      const name =
        String(item.productName ?? '').trim() || String(item.sapCode ?? '').trim();
      const qty = Number(item.quantity);
      if (!name || !Number.isFinite(qty) || qty <= 0) return '';
      const qtyLabel = formatWarehouseTransferNotificationQty(qty);
      const unit = String(item.unit ?? '').trim();
      return unit ? `${name} × ${qtyLabel} ${unit}` : `${name} × ${qtyLabel}`;
    })
    .filter(Boolean);
  return parts.join(' · ');
}

export function formatWarehouseTransferNotificationMessage(params: {
  outcome: WarehouseTransferNotificationOutcome;
  confirmationKind?: WarehouseTransferConfirmationKind | null;
  items?: ReadonlyArray<WarehouseTransferNotificationItemInput> | null;
  fromLabel?: string | null;
  toLabel?: string | null;
}): string {
  const headline =
    params.outcome === 'confirmed'
      ? 'Převod materiálu byl potvrzen.'
      : params.outcome === 'rejected'
        ? 'Převod materiálu byl odmítnut. Zásoba zůstala u odesílatele.'
        : params.outcome === 'received'
          ? 'Na váš sklad byl převeden materiál.'
          : params.outcome === 'taken'
            ? 'Z vašeho skladu byl odebrán materiál.'
            : params.confirmationKind === WAREHOUSE_TRANSFER_CONFIRMATION_VYDEJ
              ? 'Kolega žádá o výdej materiálu z vašeho skladu.'
              : 'Kolega vám předává materiál.';
  const itemsLine = formatWarehouseTransferItemSummary(params.items);
  const fromLabel = String(params.fromLabel ?? '').trim();
  const toLabel = String(params.toLabel ?? '').trim();
  const routeLine = fromLabel || toLabel ? `${fromLabel || '—'} → ${toLabel || '—'}` : '';
  return [headline, itemsLine, routeLine].filter(Boolean).join('\n');
}

function formatWarehouseTransferNotificationQty(qty: number): string {
  if (Number.isInteger(qty)) return String(qty);
  return String(Math.round(qty * 1000) / 1000).replace('.', ',');
}
